/**
 * snapdom engine — primary rasterizer.
 *
 * Captures once to an SVG-foreignObject data URL held as a decoded
 * HTMLImageElement, then draws page regions from it. Because the browser's
 * own renderer paints the SVG, modern CSS the html2canvas family can't parse
 * (conic/repeating gradients, oklch()/color-mix(), background-clip: text)
 * renders natively — and drawImage re-rasterizes the vector content at the
 * destination scale, so 2x region draws stay sharp (verified in the Phase-3
 * spike).
 *
 * SECURITY (spike-verified, plus our own guarantees upstream): the content
 * fed here is either the pre-sanitized preview snapshot or fallback HTML that
 * export-capture strips of scripts/inline handlers, and it lives in a
 * sandboxed no-scripts iframe. SVG-as-image rendering executes no script by
 * spec. snapdom may re-fetch not-yet-inlined assets with normal (credentialed)
 * page semantics — identical to the preview iframe loading them, not an
 * escalation; the snapshot path has them pre-inlined as data URIs anyway.
 */
import { snapdom } from '@zumer/snapdom'
import { ExportFault, markExportStage } from '../export-errors.js'
import { MAX_CANVAS_DIM, MAX_SOURCE_HEIGHT, type RasterEngine, type RasterizeRequest, type RasterSource, type Region } from './types.js'

// Firefox canvas draws of an SVG image drop background-clip:text glyphs on
// the FIRST paint after the SVG's internal document (re)parses, and Gecko
// caches that bad raster per destination size — gradient titles vanished
// from FF exports while a delayed draw at a fresh size rendered them
// (measured 2026-07-14: draw at +0ms after decode → missing; +30-60ms →
// correct; after ~100ms idle Gecko discards the internal document and the
// next draw first-paints bad again). The FF path therefore warms the image
// up with a throwaway draw (forces the re-parse), waits inside the good
// window, then rasterizes ONCE per scale into an ImageBitmap that page
// strips crop from. Chromium/WebKit keep the direct-draw path: they don't
// exhibit the race, and Chromium's createImageBitmap of these SVGs taints
// the canvas (the data-URL SVG carries a <base href> to another origin),
// which would break toBlob.
const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox')
const FF_SVG_SETTLE_MS = 60

export const snapdomEngine: RasterEngine = {
  name: 'snapdom',

  async capture(req: RasterizeRequest): Promise<RasterSource> {
    if (req.heightCss > MAX_SOURCE_HEIGHT) {
      throw new ExportFault(`snapdom: content height ${Math.round(req.heightCss)}px exceeds SVG-image safety cap (${MAX_SOURCE_HEIGHT}px)`, 'canvas-limit', 'rasterize')
    }
    const tCap = performance.now()
    const result = await snapdom(req.el, {
      embedFonts: true,
      backgroundColor: null as unknown as string, // transparent — compositing owns the bg
      cache: 'soft',
      // Never wait on the network for images the page itself hasn't loaded:
      // an unloaded <img> paints as a blank box in the browser, and that is
      // exactly what the capture should show. Without this snapdom re-fetches
      // pending/hanging sources (~3s internal timeout EACH) and stalls the
      // preview; loaded images re-fetch instantly from the HTTP cache.
      // 'hide' keeps the element's layout box (visibility:hidden), so nothing
      // shifts — and a late-loading image appears in the post-settle re-render
      // because the predicate re-evaluates on every capture.
      filter: (el: Element) =>
        !(el.tagName === 'IMG' && !((el as HTMLImageElement).complete && (el as HTMLImageElement).naturalWidth > 0)),
      filterMode: 'hide',
    })
    markExportStage('sdCap', performance.now() - tCap)
    const tSvg = performance.now()
    const img = await result.toSvg()
    markExportStage('sdSvg', performance.now() - tSvg)
    // Ensure the SVG image is decodable before we claim success (tall/complex
    // captures can build a data URL that then fails to decode).
    const tDec = performance.now()
    if (typeof img.decode === 'function') await img.decode()
    markExportStage('sdDec', performance.now() - tDec)

    let disposed = false
    // Firefox path: one full-size bitmap per requested scale, page strips crop
    // from it (see isFirefox note above). Cached — the paged PDF path draws
    // many strips at one scale.
    const bitmaps = new Map<number, Promise<ImageBitmap>>()
    const bitmapForScale = (scale: number): Promise<ImageBitmap> => {
      let p = bitmaps.get(scale)
      if (!p) {
        p = (async () => {
          // Warm-up: force Gecko to (re)parse the SVG's internal document,
          // then wait inside the settle window before the real rasterization
          // (see the isFirefox note above).
          const scratch = document.createElement('canvas')
          scratch.width = scratch.height = 8
          scratch.getContext('2d')!.drawImage(img, 0, 0, 8, 8)
          await new Promise((r) => setTimeout(r, FF_SVG_SETTLE_MS))
          return createImageBitmap(img, {
            resizeWidth: Math.max(1, Math.round(req.widthCss * scale)),
            resizeHeight: Math.max(1, Math.round(req.heightCss * scale)),
            resizeQuality: 'high',
          })
        })()
        bitmaps.set(scale, p)
      }
      return p
    }
    return {
      widthCss: req.widthCss,
      heightCss: req.heightCss,
      engine: 'snapdom',
      async toCanvas(region: Region, scale: number): Promise<HTMLCanvasElement> {
        if (disposed) throw new Error('snapdom: RasterSource used after dispose')
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, Math.round(region.w * scale))
        canvas.height = Math.max(1, Math.round(region.h * scale))
        const ctx = canvas.getContext('2d')!
        // The bitmap path only fits while the full-content raster stays under
        // the canvas dimension cap; extreme documents fall back to the direct
        // draw (and, on Firefox, the clip-text flake it carries).
        const bitmapFits = req.widthCss * scale <= MAX_CANVAS_DIM && req.heightCss * scale <= MAX_CANVAS_DIM
        markExportStage('sdBmp', isFirefox && bitmapFits ? 1 : 0)
        if (isFirefox && bitmapFits) {
          const bmp = await bitmapForScale(scale)
          ctx.drawImage(bmp,
            Math.round(region.x * scale), Math.round(region.y * scale),
            Math.round(region.w * scale), Math.round(region.h * scale),
            0, 0, canvas.width, canvas.height)
        } else {
          // drawImage from an SVG image re-renders at destination resolution.
          ctx.drawImage(img, region.x, region.y, region.w, region.h, 0, 0, canvas.width, canvas.height)
        }
        return canvas
      },
      dispose() {
        disposed = true
        img.src = '' // release the (potentially large) decoded SVG image
        for (const p of bitmaps.values()) p.then((b) => b.close()).catch(() => {})
        bitmaps.clear()
      },
    }
  },
}
